import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./AdminClubsSearch.module.scss";

const LANGS = [
  { value: "", label: "All languages" },
  { value: "en", label: "English (en)" },
  { value: "si", label: "Sinhala (si)" },
  { value: "ta", label: "Tamil (ta)" },
  { value: "hi", label: "Hindi (hi)" },
  { value: "fr", label: "French (fr)" },
];

const STATUSES = [
  { value: "", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

function clampInt(v, fallback, min, max) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(n)));
}

export default function AdminClubsSearch() {
  const [sp, setSp] = useSearchParams();

  // URL-driven state
  const qParam = sp.get("q") || "";
  const langParam = sp.get("language") || "";
  const statusParam = sp.get("status") || "";
  const cityIdsParam = sp.get("cityIds") || "";
  const streetIdsParam = sp.get("streetIds") || "";
  const pageParam = clampInt(sp.get("page"), 1, 1, 9999);
  const limitParam = clampInt(sp.get("limit"), 20, 5, 100);

  // Local UI state
  const [q, setQ] = useState(qParam);
  const [language, setLanguage] = useState(langParam);
  const [status, setStatus] = useState(statusParam);
  const [cityIds, setCityIds] = useState(cityIdsParam);
  const [streetIds, setStreetIds] = useState(streetIdsParam);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    limit: limitParam,
    total: 0,
    pages: 1,
  });
  const [error, setError] = useState("");

  // Sync from URL
  useEffect(() => setQ(qParam), [qParam]);
  useEffect(() => setLanguage(langParam), [langParam]);
  useEffect(() => setStatus(statusParam), [statusParam]);
  useEffect(() => setCityIds(cityIdsParam), [cityIdsParam]);
  useEffect(() => setStreetIds(streetIdsParam), [streetIdsParam]);

  const queryObj = useMemo(() => {
    const obj = {
      q: qParam.trim(),
      language: langParam.trim(),
      status: statusParam.trim(),
      cityIds: cityIdsParam.trim(),
      streetIds: streetIdsParam.trim(),
      page: String(pageParam),
      limit: String(limitParam),
    };

    Object.keys(obj).forEach((k) => {
      if (!obj[k]) delete obj[k];
    });

    return obj;
  }, [
    qParam,
    langParam,
    statusParam,
    cityIdsParam,
    streetIdsParam,
    pageParam,
    limitParam,
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Adjust if your route differs (example: /v1/admin/clubs/search)
      const res = await api.get("/v1/admin/cities/clubs/search", {
        params: queryObj,
      });

      setRows(res.data?.clubs || []);
      setMeta(
        res.data?.meta || {
          page: pageParam,
          limit: limitParam,
          total: 0,
          pages: 1,
        }
      );
    } catch (e) {
      setRows([]);
      setMeta({ page: 1, limit: limitParam, total: 0, pages: 1 });
      setError(e?.response?.data?.message || "Failed to load clubs.");
    } finally {
      setLoading(false);
    }
  }, [queryObj, pageParam, limitParam]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounce q typing → update URL
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(sp);

      if (q.trim()) next.set("q", q.trim());
      else next.delete("q");

      next.set("page", "1");
      setSp(next, { replace: true });
    }, 350);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const applyFilters = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams(sp);

    if (language) next.set("language", language);
    else next.delete("language");

    if (status) next.set("status", status);
    else next.delete("status");

    const cleanCsv = (val) =>
      val
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .join(",");

    const cleanedCityIds = cleanCsv(cityIds);
    const cleanedStreetIds = cleanCsv(streetIds);

    if (cleanedCityIds) next.set("cityIds", cleanedCityIds);
    else next.delete("cityIds");

    if (cleanedStreetIds) next.set("streetIds", cleanedStreetIds);
    else next.delete("streetIds");

    next.set("page", "1");
    next.set("limit", String(limitParam));

    setSp(next, { replace: true });
  };

  const clearAll = () => {
    setQ("");
    setLanguage("");
    setStatus("");
    setCityIds("");
    setStreetIds("");
    setSp(new URLSearchParams({ page: "1", limit: String(limitParam) }), {
      replace: true,
    });
  };

  const goPage = (p) => {
    const next = new URLSearchParams(sp);
    next.set("page", String(p));
    setSp(next, { replace: true });
  };

  const canPrev = meta.page > 1;
  const canNext = meta.page < meta.pages;

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.headerTop}>
          <div>
            <h1 className={cls.title}>Clubs Search</h1>
            <p className={cls.subtitle}>
              Search clubs, filter by city, street and language, and jump to the
              related city/street admin pages.
            </p>
          </div>

          <div className={cls.headerActions}>
            <button
              type="button"
              className="nu-btn nu-btn--ghost"
              onClick={clearAll}
            >
              Clear
            </button>
            <button
              type="button"
              className="nu-btn nu-btn--blue"
              onClick={fetchData}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className={cls.searchRow}>
          <div className={cls.searchBox}>
            <span className={cls.searchIcon} aria-hidden="true">
              ⌕
            </span>
            <input
              className={cls.searchInput}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search club name..."
              autoComplete="off"
            />
          </div>

          <form className={cls.filters} onSubmit={applyFilters}>
            <select
              className={cls.select}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              aria-label="Language"
            >
              {LANGS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>

            <select
              className={cls.select}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Status"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>

            <input
              className={cls.cityIdsInput}
              value={cityIds}
              onChange={(e) => setCityIds(e.target.value)}
              placeholder="City IDs (comma separated)"
              autoComplete="off"
            />

            <input
              className={cls.cityIdsInput}
              value={streetIds}
              onChange={(e) => setStreetIds(e.target.value)}
              placeholder="Street IDs (comma separated)"
              autoComplete="off"
            />

            <button
              type="submit"
              className="nu-btn nu-btn--green"
              disabled={loading}
            >
              Apply
            </button>
          </form>
        </div>

        <div className={cls.metaRow}>
          <div className={cls.metaPill}>
            Total: <b>{meta.total}</b>
          </div>
          <div className={cls.metaPill}>
            Page: <b>{meta.page}</b> / {meta.pages}
          </div>
          <div className={cls.metaPill}>
            Limit: <b>{meta.limit}</b>
          </div>
        </div>
      </header>

      {error ? <div className={cls.errorBox}>{error}</div> : null}

      <section className={cls.results}>
        {loading ? (
          <div className={cls.stateBox}>Loading clubs…</div>
        ) : rows.length === 0 ? (
          <div className={cls.stateBox}>No clubs found.</div>
        ) : (
          <div className={cls.grid}>
            {rows.map((c) => {
              const cityLink = c.cityId
                ? `/admin/cities/single/${c.cityId}`
                : "#";
              const streetLink =
                c.cityId && c.streetId
                  ? `/admin/cities/single/${c.cityId}/streets/${c.streetId}`
                  : "#";

              // If you have a club single route, tell me and I’ll switch this.
              const viewClubsLink =
                c.cityId && c.streetId
                  ? `/admin/cities/single/${c.cityId}/streets/${c.streetId}/clubs/${c.clubId}`
                  : "#";

              return (
                <article key={c.clubId} className={cls.card}>
                  <div className={cls.cardTop}>
                    <div className={cls.avatarWrap}>
                      {c.avatarThumb?.url ? (
                        <img
                          className={cls.avatar}
                          src={c.avatarThumb.url}
                          alt={`${c.clubName || "Club"} thumbnail`}
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className={cls.avatarFallback}
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    <div className={cls.main}>
                      <div className={cls.row1}>
                        <h3 className={cls.streetName}>
                          {c.clubName || "Untitled club"}
                        </h3>

                        <span
                          className={`${cls.badge} ${
                            c.clubStatus === "open"
                              ? cls.badgeOpen
                              : cls.badgeClosed
                          }`}
                        >
                          {String(c.clubStatus || "").toUpperCase() || "—"}
                        </span>
                      </div>

                      <div className={cls.row2}>
                        <span className={cls.kv}>
                          <span className={cls.k}>Language:</span>{" "}
                          <span className={cls.v}>{c.clubLanguage || "—"}</span>
                        </span>

                        <span className={cls.dot} aria-hidden="true">
                          •
                        </span>

                        <span className={cls.kv}>
                          <span className={cls.k}>City:</span>{" "}
                          {c.cityId ? (
                            <Link className={cls.cityLink} to={cityLink}>
                              {c.cityName || c.cityId}
                            </Link>
                          ) : (
                            <span className={cls.v}>{c.cityName || "—"}</span>
                          )}
                        </span>

                        <span className={cls.dot} aria-hidden="true">
                          •
                        </span>

                        <span className={cls.kv}>
                          <span className={cls.k}>Street:</span>{" "}
                          {c.cityId && c.streetId ? (
                            <Link className={cls.cityLink} to={streetLink}>
                              {c.streetName || c.streetId}
                            </Link>
                          ) : (
                            <span className={cls.v}>{c.streetName || "—"}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={cls.cardFooter}>
                    <Link className="nu-btn nu-btn--blue" to={viewClubsLink}>
                      View
                    </Link>

                    {c.cityId && c.streetId ? (
                      <Link
                        className="nu-btn nu-btn--green"
                        to={`/admin/cities/single/${c.cityId}/streets/${c.streetId}`}
                      >
                        Visit Street
                      </Link>
                    ) : (
                      <button
                        className="nu-btn nu-btn--green"
                        type="button"
                        disabled
                      >
                        Visit Street
                      </button>
                    )}

                    {c.cityId ? (
                      <Link className="nu-btn nu-btn--ghost" to={cityLink}>
                        Open City
                      </Link>
                    ) : (
                      <button
                        className="nu-btn nu-btn--ghost"
                        type="button"
                        disabled
                      >
                        Open City
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <footer className={cls.pager}>
        <button
          type="button"
          className="nu-btn nu-btn--ghost"
          onClick={() => goPage(1)}
          disabled={!canPrev || loading}
        >
          First
        </button>
        <button
          type="button"
          className="nu-btn nu-btn--ghost"
          onClick={() => goPage(meta.page - 1)}
          disabled={!canPrev || loading}
        >
          Prev
        </button>

        <div className={cls.pagerInfo}>
          Page <b>{meta.page}</b> of {meta.pages}
        </div>

        <button
          type="button"
          className="nu-btn nu-btn--ghost"
          onClick={() => goPage(meta.page + 1)}
          disabled={!canNext || loading}
        >
          Next
        </button>
        <button
          type="button"
          className="nu-btn nu-btn--ghost"
          onClick={() => goPage(meta.pages)}
          disabled={!canNext || loading}
        >
          Last
        </button>
      </footer>

      <div className={cls.bottomNote}>
        Document generated | BitElegant AI Solutions
      </div>
    </div>
  );
}
